import { noop } from '@aloft/toolkit-shared';
import { Button, Flex } from '@aloft/toolkit-sui';

import { useState } from 'react';
import { useNavigate } from 'react-router';

import { EnvironmentFilter } from '../../../../components/EnvironmentFilter/EnvironmentFilter.tsx';
import { useToaster } from '../../../../hooks/useToaster/useToaster.tsx';
import { selectAccount } from '../../../../redux/features/selectors/accountSelectors.ts';
import { useAppSelector } from '../../../../redux/hooks.ts';
import { whatEnv } from '../../../../utils/whatEnv.ts';
import { useCreateMockHybridConversionOrder } from '../OrderTable/hooks/useCreateMockHybridConversionOrder.ts';
import { useCreateMockRevisionOrder } from '../OrderTable/hooks/useCreateMockRevisionOrder.ts';
import { useDeleteOrderModal } from './useDeleteOrderModal.tsx';

export const OrderActionsMenu = ({
  orderID,
  address,
  isArchived,
  isTraining,
  isCompleted,
  isInspectifyOrder,
  isMock,
  updateOrder,
  deleteOrder,
  downloadReport,
  openDocumentsPage,
}: {
  orderID: string;
  address: string;
  isArchived?: boolean;
  isTraining?: boolean;
  isCompleted?: boolean;
  isInspectifyOrder: boolean;
  isMock: boolean;
  updateOrder: () => void;
  deleteOrder: () => void;
  downloadReport: () => void;
  openDocumentsPage: () => void;
}) => {
  const { Modal: deleteOrderModal, openModal: openDeleteOrderModal } = useDeleteOrderModal(
    address,
    deleteOrder,
  );
  const account = useAppSelector(selectAccount);
  const createMockRevisionOrder = useCreateMockRevisionOrder();
  const createMockHybridConversionOrder = useCreateMockHybridConversionOrder();
  const navigate = useNavigate();
  const toast = useToaster();
  const [isCreatingRevision, setIsCreatingRevision] = useState(false);
  const [isCreatingHybrid, setIsCreatingHybrid] = useState(false);
  const canCreateMockDerivatives =
    isMock && (whatEnv() === 'local' || !!account.isAdmin);

  const handleMockRevision = async () => {
    setIsCreatingRevision(true);
    try {
      const revisionOrderID = await createMockRevisionOrder(orderID);
      navigate(`/order/${revisionOrderID}/paired-cost/details`);
    } catch (err) {
      console.error(err);
      toast({
        type: 'error',
        id: 'mock-revision-error',
        title: 'Failed to create mock revision order',
      });
    } finally {
      setIsCreatingRevision(false);
    }
  };

  const handleMockHybridConversion = async () => {
    setIsCreatingHybrid(true);
    try {
      const hybridOrderID = await createMockHybridConversionOrder(orderID);
      navigate(`/order/${hybridOrderID}/paired-cost/details`);
    } catch (err) {
      console.error(err);
      toast({
        type: 'error',
        id: 'mock-hybrid-conversion-error',
        title: 'Failed to create mock hybrid conversion order',
      });
    } finally {
      setIsCreatingHybrid(false);
    }
  };

  if (isInspectifyOrder && !isCompleted && !account.isAdmin) {
    return null;
  }

  return (
    <EnvironmentFilter environments={['regularMode']}>
      <Flex className="archive-button-wrapper">
        <span className="archive-button-visibility-wrapper">
          <Flex className="archive-button-layout">
            {isCompleted ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    downloadReport();
                  }}
                  text="Download"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    openDocumentsPage();
                  }}
                  text="Documents"
                />
                {canCreateMockDerivatives && (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={isCreatingRevision}
                      onClick={(e) => {
                        e.preventDefault();
                        handleMockRevision().catch(noop);
                      }}
                      text={isCreatingRevision ? 'Creating...' : 'Mock Revision'}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={isCreatingHybrid}
                      onClick={(e) => {
                        e.preventDefault();
                        handleMockHybridConversion().catch(noop);
                      }}
                      text={isCreatingHybrid ? 'Creating...' : 'Mock Hybrid Conversion'}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    updateOrder();
                  }}
                  text={isArchived ? 'Unarchive' : 'Archive'}
                />
                {!isTraining && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      openDeleteOrderModal();
                    }}
                    text="Delete"
                  />
                )}
              </>
            )}
          </Flex>
        </span>
      </Flex>
      {deleteOrderModal}
    </EnvironmentFilter>
  );
};
